import { Route } from 'react-router-dom';
import { JourneyShell } from '../journeys/JourneyShell.js';
import { PostKioskResolutionContext } from './PostKioskResolutionContext.js';
import { PostKioskFailureRoute } from '../journeys/PostKioskFailureRoute.js';
import { useResolvePostKioskToken } from './checkoutApi.js';
import { usePostKioskGuestLandStaging } from './usePostKioskGuestLandStaging.js';
import { Card } from '../../shared/ui/Card.js';
import { CustomerFormFieldBoundary } from '../../shared/ui/FormField.js';
import { EntitlementRouteGuard } from '../entitlements/customerEntitlementGuards.js';
import { FeatureFlagRouteGuard } from '../entitlements/FeatureFlagRouteGuard.js';
import { DonateLegacyPathRedirect } from '../donation/DonateLegacyPathRedirect.js';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  PhoneFirstProductJourneyLifecycle,
} from '../journeys/components/TierAJourneyLifecycle.js';
import {
  PostKioskDonationJourneyLifecycle,
  PostKioskProductJourneyLifecycle,
  PostKioskResolverLifecycle,
} from '../journeys/components/PostKioskJourneyLifecycle.js';
import { CheckoutCompleteRouteLifecycle } from './CheckoutCompleteRouteLifecycle.js';
import { CheckoutCommerceEntitlementGate } from './CheckoutCommerceEntitlementGate.js';
import { CheckoutScopeProviders } from './CheckoutScopeProviders.js';
import { withRegisteredScreen } from '../../app/routes/registryGuard.js';
import { lazy, useMemo } from 'react';
import {
  RemountBoundary,
  RouteErrorFallback,
} from '../../shared/components/ErrorBoundary/index.js';
import { ErrorIsolationProbe } from '../../test/e2e/errorIsolationProbe.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { resolvePostAuthFallbackPath } from '../../navigation/navigationEngine.js';
import {
  usePublishHierarchicalBackFlow,
  type HierarchicalBackFlowPublishInput,
} from '../../shared/context/HierarchicalBackFlowContext.js';

export { WAVE2_LAZY_ROUTE_IDS, wave2LazyImportLoaders } from './lazyRouteInventory.js';

function withCustomerFormFieldSurface(element: JSX.Element): JSX.Element {
  return <CustomerFormFieldBoundary>{element}</CustomerFormFieldBoundary>;
}

/** L3 remount — one wrap site for checkout / post-kiosk journey elements. */
function withCheckoutRemount(element: JSX.Element): JSX.Element {
  return (
    <RemountBoundary
      feature="checkout"
      fallback={({ onRetry, feature }) => (
        <RouteErrorFallback
          onRetry={onRetry}
          feature={feature}
          testId="customer-eb-l3-checkout"
        />
      )}
    >
      <ErrorIsolationProbe feature="checkout" />
      {element}
    </RemountBoundary>
  );
}

const CheckoutHandoffPageLazy = lazy(() =>
  import('./CheckoutHandoffPage.js').then((module) => ({
    default: module.CheckoutHandoffPage,
  }))
);

function PostKioskResolverInner(): JSX.Element {
  const { t } = useTranslation('customer');
  const tenantCode = useTenantCode();
  // Invalid / loading / expired resolver chrome → shop escape (not a paid terminal).
  const shopFallback = resolvePostAuthFallbackPath(tenantCode);
  const [params] = useSearchParams();
  const token = params.get('token');
  const resolved = useResolvePostKioskToken(token);
  const publishInput = useMemo((): HierarchicalBackFlowPublishInput | null => {
    if (token === null || token.length === 0) {
      return {
        flowKind: 'checkout',
        flowStep: 'post-kiosk-invalid',
        paymentActive: false,
        stepBackTo: shopFallback,
        onStepBack: null,
      };
    }
    if (resolved.isLoading) {
      return {
        flowKind: 'checkout',
        flowStep: 'post-kiosk-loading',
        paymentActive: false,
        stepBackTo: shopFallback,
        onStepBack: null,
      };
    }
    if (resolved.isError || resolved.data === undefined) {
      return {
        flowKind: 'checkout',
        flowStep: 'post-kiosk-expired',
        paymentActive: false,
        stepBackTo: shopFallback,
        onStepBack: null,
      };
    }
    return null;
  }, [resolved.data, resolved.isError, resolved.isLoading, shopFallback, token]);
  usePublishHierarchicalBackFlow(publishInput);

  if (token === null || token.length === 0) {
    return (
      <JourneyShell
        title={t('app.postKiosk.invalidLinkTitle')}
        context="POST_KIOSK_PRODUCT"
        backTo={shopFallback}
        backLabel={t('checkout.success.backToShop')}
      >
        <Card padded>
          <p className="text-sm text-[var(--color-danger)]">
            {t('app.postKiosk.invalidLinkBody')}
          </p>
        </Card>
      </JourneyShell>
    );
  }

  if (resolved.isLoading) {
    return (
      <JourneyShell
        title={t('app.postKiosk.loadingTitle')}
        context="POST_KIOSK_PRODUCT"
        backTo={shopFallback}
        backLabel={t('checkout.success.backToShop')}
      >
        <Card padded>
          <p className="text-sm text-[var(--color-on-surface-muted)]">
            {t('app.postKiosk.loadingBody')}
          </p>
        </Card>
      </JourneyShell>
    );
  }

  if (resolved.isError || resolved.data === undefined) {
    return (
      <JourneyShell
        title={t('app.postKiosk.expiredTitle')}
        context="POST_KIOSK_PRODUCT"
        backTo={shopFallback}
        backLabel={t('checkout.success.backToShop')}
      >
        <Card padded>
          <p className="text-sm text-[var(--color-danger)]">
            {t('app.postKiosk.expiredBody')}
          </p>
        </Card>
      </JourneyShell>
    );
  }

  return (
    <PostKioskResolvedLand resolution={resolved.data} />
  );
}

function PostKioskResolvedLand({
  resolution,
}: {
  readonly resolution: NonNullable<
    ReturnType<typeof useResolvePostKioskToken>['data']
  >;
}): JSX.Element {
  usePostKioskGuestLandStaging(resolution);

  if (resolution.purposeType === 'DONATION') {
    return (
      <EntitlementRouteGuard blockKeys={['donation']} mode="inline-unavailable">
        <FeatureFlagRouteGuard requires={{ master: true, anyOf: ['donate'] }} mode="inline-unavailable">
          <PostKioskResolutionContext.Provider value={resolution}>
            <PostKioskDonationJourneyLifecycle />
          </PostKioskResolutionContext.Provider>
        </FeatureFlagRouteGuard>
      </EntitlementRouteGuard>
    );
  }
  return (
    <PostKioskResolutionContext.Provider value={resolution}>
      <PostKioskProductJourneyLifecycle />
    </PostKioskResolutionContext.Provider>
  );
}

function withCheckoutScope(element: JSX.Element): JSX.Element {
  return <CheckoutScopeProviders>{element}</CheckoutScopeProviders>;
}

export function CheckoutJourneyRoutes(): JSX.Element {
  return (
    <>
      <Route
        path="post-kiosk"
        element={withCheckoutScope(
          withCheckoutRemount(
            withCustomerFormFieldSurface(
              withRegisteredScreen(
                'MS-COM-033',
                <CheckoutCommerceEntitlementGate>
                  <PostKioskResolverLifecycle>
                    <PostKioskResolverInner />
                  </PostKioskResolverLifecycle>
                </CheckoutCommerceEntitlementGate>,
              ),
            ),
          ),
        )}
      />
      <Route
        path="post-kiosk-failure"
        element={withCheckoutScope(
          withCheckoutRemount(
            withRegisteredScreen('MS-COM-034', <PostKioskFailureRoute />),
          ),
        )}
      />
      <Route
        path="checkout/handoff"
        element={withCheckoutScope(
          withCheckoutRemount(
            withRegisteredScreen(
              'MS-COM-030',
              <CheckoutCommerceEntitlementGate>
                <CheckoutHandoffPageLazy />
              </CheckoutCommerceEntitlementGate>,
            ),
          ),
        )}
      />
      <Route
        path="checkout"
        element={withCheckoutScope(
          withCheckoutRemount(
            withCustomerFormFieldSurface(
              withRegisteredScreen(
                'MS-COM-031',
                <CheckoutCommerceEntitlementGate>
                  <PhoneFirstProductJourneyLifecycle />
                </CheckoutCommerceEntitlementGate>,
              ),
            ),
          ),
        )}
      />
      <Route
        path="checkout/complete"
        element={withCheckoutScope(
          withCheckoutRemount(
            withRegisteredScreen(
              'MS-COM-032',
              <CheckoutCommerceEntitlementGate>
                <CheckoutCompleteRouteLifecycle />
              </CheckoutCommerceEntitlementGate>,
            ),
          ),
        )}
      />
      <Route
        path="handoff"
        element={withCheckoutScope(
          withCheckoutRemount(
            withRegisteredScreen(
              'MS-COM-030',
              <CheckoutCommerceEntitlementGate>
                <CheckoutHandoffPageLazy />
              </CheckoutCommerceEntitlementGate>,
            ),
          ),
        )}
      />
      {/* ADR-012 — legacy checkout-scoped donate mount redirects to canonical retail tree */}
      <Route path="donate/*" element={<DonateLegacyPathRedirect />} />
    </>
  );
}
